import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Card, CardContent } from "./ui/card";
import { Heart, MessageCircle, Mic, Send, Quote, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";

export const CommunityStories = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    story: "",
    wantsPodcast: false,
  });

  // Fetch from the secure view that excludes email addresses
  const { data: stories, refetch } = useQuery({
    queryKey: ["community-stories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("approved_community_stories")
        .select("id, name, story, created_at, wants_podcast_interview")
        .order("created_at", { ascending: false })
        .limit(6);
      
      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.story.trim()) {
      toast({
        title: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.from("community_stories").insert({
        name: formData.name.trim(),
        email: formData.email.trim(),
        story: formData.story.trim(),
        wants_podcast_interview: formData.wantsPodcast,
      });

      if (error) throw error;

      toast({
        title: "Thank you for sharing!",
        description: formData.wantsPodcast 
          ? "We'll review your story and reach out about podcast opportunities!"
          : "Your story has been submitted for review.",
      });

      setFormData({ name: "", email: "", story: "", wantsPodcast: false });
      refetch();
    } catch (error) {
      console.error("Error submitting story:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-story-peace/10">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <MessageCircle className="w-6 h-6 text-story-hope" />
            <span className="text-story-hope font-semibold uppercase tracking-wider text-sm">
              Community Voices
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-story-germany mb-4">
            Share Your Story
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have an uplifting story of hope, resilience, or healing? Share it with our community. 
            Your story could inspire others and you might be featured on our podcast 
            <span className="font-semibold text-story-germany"> "Healing Through Progress"</span>.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Submission Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-2 border-story-hope/20 shadow-xl bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-story-hope/20 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-story-hope" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-story-germany">Your Story Matters</h3>
                    <p className="text-gray-500 text-sm">Every journey has power to inspire</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Name
                    </label>
                    <Input
                      placeholder="How should we credit you?"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="border-gray-200 focus:border-story-hope focus:ring-story-hope"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="border-gray-200 focus:border-story-hope focus:ring-story-hope"
                      maxLength={255}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Story of Hope
                    </label>
                    <Textarea
                      placeholder="Share your journey of resilience, healing, or the moment that changed everything..."
                      value={formData.story}
                      onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                      className="border-gray-200 focus:border-story-hope focus:ring-story-hope min-h-[150px] resize-none"
                      maxLength={2000}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {formData.story.length}/2000 characters
                    </p>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-story-hope/10 rounded-lg border border-story-hope/20">
                    <Checkbox
                      id="podcast"
                      checked={formData.wantsPodcast}
                      onCheckedChange={(checked) => 
                        setFormData({ ...formData, wantsPodcast: checked === true })
                      }
                      className="mt-0.5 data-[state=checked]:bg-story-hope data-[state=checked]:border-story-hope"
                    />
                    <label htmlFor="podcast" className="cursor-pointer">
                      <div className="flex items-center gap-2 font-medium text-story-germany">
                        <Mic className="w-4 h-4" />
                        I'd love to share my story on the podcast!
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Be a guest on "Healing Through Progress" and inspire thousands of listeners
                      </p>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-story-hope hover:bg-story-hope/90 text-black font-semibold py-6 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Share Your Story
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Community Stories Display */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <Quote className="w-8 h-8 text-story-peace" />
              <h3 className="font-serif text-2xl text-story-germany">Voices of Hope</h3>
            </div>

            <AnimatePresence>
              {stories && stories.length > 0 ? (
                stories.map((story, index) => (
                  <motion.div
                    key={story.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="border border-gray-100 shadow-md hover:shadow-lg transition-shadow duration-300 bg-white/80 backdrop-blur-sm">
                      <CardContent className="p-6">
                        <Quote className="w-8 h-8 text-story-hope/30 mb-3" />
                        <p className="text-gray-700 leading-relaxed mb-4 line-clamp-4">
                          {story.story}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-story-germany">{story.name}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(story.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">Be the first to share your story!</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Your words could inspire someone today
                  </p>
                </div>
              )}
            </AnimatePresence>

            {/* Podcast CTA */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="mt-8 p-6 bg-gradient-to-br from-story-germany to-story-sudan rounded-xl text-white"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Mic className="w-7 h-7" />
                </div>
                <div>
                  <h4 className="font-serif text-xl mb-2">Healing Through Progress</h4>
                  <p className="text-white/80 text-sm leading-relaxed">
                    Join Tachen on his podcast where guests share their journeys of overcoming adversity, 
                    finding hope, and healing through progress. Your story could inspire thousands.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
